<script setup lang="ts">
import { computed } from 'vue';
import type { VAvatar } from 'vuetify/components';
import { BIcon } from '@/design-system';

type PropsType = {
    size?: string | number;
    color?: string;
    image?: string;
    icon?: string;
    text?: string;
    rounded?: string | number | boolean;
    variant?: VAvatar['$props']['variant'];
    clickable?: boolean;
    class?: string;
    borderColor?: string;
    textColor?: string;
};

const props = withDefaults(defineProps<PropsType>(), {
    size: 40,
    rounded: true,
    variant: 'flat',
    clickable: false,
    borderColor: '#ffffff',
    textColor: 'white',
});

const emit = defineEmits<{
    click: [event: MouseEvent];
}>();

defineOptions({ inheritAttrs: false });

const handleClick = (event: MouseEvent) => {
    if (props.clickable) {
        emit('click', event);
    }
};

const getClasses = computed(() => {
    return {
        'cursor-pointer': props.clickable,
        [props.class || '']: !!props.class,
    };
});

const avatarStyles = computed(() => ({ '--avatar-border-color': `rgb(var(--v-theme-${props.borderColor}))` }));
</script>

<template>
    <VAvatar
        :size="size"
        :color="color"
        rounded="circle"
        :variant="variant"
        :class="getClasses"
        :border="text ? true : false"
        v-bind="$attrs"
        :style="avatarStyles"
        class="b-avatar"
        @click="handleClick"
    >
        <VImg
            v-if="image"
            :src="image"
            :alt="text || 'Avatar'"
        />
        <BIcon
            v-else-if="icon"
            :icon="icon"
            color="white"
        />
        <span
            v-else-if="text"
            class="text-white font-weight-bold d-block"
            :class="'text-' + textColor"
        >
            {{ text }}
        </span>
        <BIcon
            v-else
            icon="mdi-account"
            color="white"
        />
    </VAvatar>
</template>

<style lang="scss" scoped>
.b-avatar {
    border: 2px solid var(--avatar-border-color);
}
</style>
